import React, { useEffect, useState } from "react";
import {
  Boxes,
  SprayCan,
  AlertTriangle,
  PackageX,
  Loader2,
  X as XIcon,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { toast } from "sonner";

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="cm-card p-5">
      <header className="flex items-start gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
          <Icon size={16} />
        </div>
        <div>
          <h3 className="font-semibold text-slate-800 leading-tight">{title}</h3>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/**
 * Materiais da equipe — visível apenas para LÍDERES de equipe.
 * Mostra (somente leitura) os equipamentos que estão com a equipe e a lista de
 * materiais de limpeza, onde o líder pode reportar "acabando" / "acabou".
 * Componente reutilizado no Perfil e na tela inicial (MobileHome).
 */
export function LeaderMaterials({ onSummary }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    try {
      const { data } = await api.get("/my-team/materials");
      setData(data);
      if (onSummary) {
        onSummary({
          isLeader: !!data.is_leader,
          openCount: Object.keys(data.my_open_alerts || {}).length,
        });
      }
    } catch {
      setData({ is_leader: false });
      if (onSummary) onSummary({ isLeader: false, openCount: 0 });
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  async function report(supplyId, level) {
    setBusyId(supplyId);
    try {
      await api.post("/supply-alerts", { supply_id: supplyId, level });
      setData((d) => {
        const next = { ...(d.my_open_alerts || {}), [supplyId]: level };
        if (onSummary) onSummary({ isLeader: true, openCount: Object.keys(next).length });
        return { ...d, my_open_alerts: next };
      });
      toast.success(
        level === "out" ? t("Avisado: material acabou") : t("Avisado: material acabando"),
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  }
  async function cancelReport(supplyId) {
    setBusyId(supplyId);
    try {
      await api.delete(`/supply-alerts/me/${supplyId}`);
      setData((d) => {
        const next = { ...(d.my_open_alerts || {}) };
        delete next[supplyId];
        if (onSummary) onSummary({ isLeader: true, openCount: Object.keys(next).length });
        return { ...d, my_open_alerts: next };
      });
      toast.success(t("Aviso cancelado"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  }

  if (!data || !data.is_leader) return null;
  const myAlerts = data.my_open_alerts || {};

  return (
    <div className="space-y-4" data-testid="leader-materials">
      <Section
        icon={Boxes}
        title={t("Materiais com a equipe")}
        subtitle={t("Equipamentos sob responsabilidade da sua equipe (somente leitura).")}
      >
        {(data.equipments || []).length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="leader-equipments-empty">
            {t("Nenhum equipamento atribuído à sua equipe.")}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="leader-equipments-list">
            {data.equipments.map((e) => (
              <li
                key={e.equipment_id}
                className="flex items-center justify-between py-2.5"
                data-testid={`leader-equipment-${e.equipment_id}`}
              >
                <span className="text-sm font-medium text-slate-800">{e.name}</span>
                <span className="text-sm font-bold text-slate-900 bg-slate-100 rounded-lg px-2.5 py-0.5">
                  {e.quantity}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section
        icon={SprayCan}
        title={t("Material de limpeza")}
        subtitle={t("Avise quando um material estiver acabando ou tiver acabado. O admin é notificado para comprar.")}
      >
        {(data.supplies || []).length === 0 ? (
          <p className="text-sm text-slate-400 italic">{t("Nenhum material cadastrado.")}</p>
        ) : (
          <ul className="space-y-2" data-testid="leader-supplies-list">
            {data.supplies.map((s) => {
              const cur = myAlerts[s.id];
              return (
                <li
                  key={s.id}
                  className="rounded-xl border border-slate-200 p-3"
                  data-testid={`leader-supply-${s.id}`}
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{s.name}</div>
                      {s.brand && <div className="text-xs text-slate-400">{s.brand}</div>}
                    </div>
                    {cur ? (
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`leader-supply-status-${s.id}`}
                          className={`text-[11px] font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                            cur === "out"
                              ? "bg-rose-50 text-rose-700 border border-rose-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {cur === "out" ? <PackageX size={12} /> : <AlertTriangle size={12} />}
                          {cur === "out" ? t("Acabou") : t("Acabando")}
                        </span>
                        <button
                          data-testid={`leader-supply-cancel-${s.id}`}
                          onClick={() => cancelReport(s.id)}
                          disabled={busyId === s.id}
                          className="text-slate-400 hover:text-slate-600"
                          title={t("Cancelar aviso")}
                        >
                          {busyId === s.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <XIcon size={15} />
                          )}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`leader-supply-low-${s.id}`}
                          onClick={() => report(s.id, "low")}
                          disabled={busyId === s.id}
                          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-amber-200 text-amber-700 hover:bg-amber-50 inline-flex items-center gap-1"
                        >
                          <AlertTriangle size={13} /> {t("Acabando")}
                        </button>
                        <button
                          data-testid={`leader-supply-out-${s.id}`}
                          onClick={() => report(s.id, "out")}
                          disabled={busyId === s.id}
                          className="text-xs font-semibold px-2.5 py-1.5 rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1"
                        >
                          <PackageX size={13} /> {t("Acabou")}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Section>
    </div>
  );
}
